import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CircularProgress, Box } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme';

// Lazy load pages for better performance
const Home = React.lazy(() => import('./pages/Home'));
const Instructors = React.lazy(() => import('./pages/Instructors'));
const InstructorProfile = React.lazy(() => import('./pages/InstructorProfile'));
const Timetable = React.lazy(() => import('./pages/Timetable'));
const Studios = React.lazy(() => import('./pages/Studios'));
const News = React.lazy(() => import('./pages/News'));
const Contact = React.lazy(() => import('./pages/Contact'));
const WhatsOn = React.lazy(() => import('./pages/WhatsOn'));
const EventDetails = React.lazy(() => import('./pages/EventDetails'));
const EventBooking = React.lazy(() => import('./pages/EventBooking'));
const Privacy = React.lazy(() => import('./pages/Privacy'));
const NotFound = React.lazy(() => import('./pages/NotFound'));

// Loading component
const LoadingSpinner = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '50vh'
    }}
  >
    <CircularProgress sx={{ color: '#4bade6' }} />
  </Box>
);

// The router keeps the window's scroll position across page changes, so a link
// clicked far down one page (e.g. the Book button at the foot of an event) would
// open the next page part-way down. Reset to the top on every new path, unless
// the link targets an anchor. 'instant' overrides the smooth scroll in index.css.
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname, hash]);
  return null;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <ScrollToTop />
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/instructors" element={<Instructors />} />
            <Route path="/instructors/:instructorId" element={<InstructorProfile />} />
            <Route path="/timetable" element={<Timetable />} />
            <Route path="/studios" element={<Studios />} />
            <Route path="/locations" element={<Navigate to="/studios" replace />} />
            <Route path="/news" element={<News />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/whats-on" element={<WhatsOn />} />
            <Route path="/whats-on/:eventId" element={<EventDetails />} />
            <Route path="/whats-on/:eventId/book" element={<EventBooking />} />
            <Route path="/privacy" element={<Privacy />} />

            {/* SEO URL Redirects - matching sitemap.xml */}
            <Route path="/bromley-studio-hire" element={<Navigate to="/studios" replace />} />
            <Route path="/dance-classes-bromley" element={<Navigate to="/timetable" replace />} />
            <Route path="/yoga-classes-bromley" element={<Navigate to="/timetable" replace />} />
            <Route path="/pilates-classes-bromley" element={<Navigate to="/timetable" replace />} />

            {/* 404 Catch-all - must be last */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </Router>
    </ThemeProvider>
  );
}

export default App;
